import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateEventInput,
  EventDto,
  UpdateEventInput,
} from '@wingseat/shared';
import { api } from '../lib/api-client';

export const eventKeys = {
  all: ['events'] as const,
  list: (upcomingOnly: boolean) =>
    ['events', 'list', { upcomingOnly }] as const,
  detail: (id: string) => ['events', 'detail', id] as const,
};

export function useEvents(upcomingOnly: boolean = false) {
  return useQuery({
    queryKey: eventKeys.list(upcomingOnly),
    queryFn: () =>
      api.get<EventDto[]>('/events', {
        query: { upcomingOnly },
      }),
  });
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: id ? eventKeys.detail(id) : ['events', 'detail', 'idle'],
    queryFn: () => api.get<EventDto>(`/events/${id!}`),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventInput) =>
      api.post<EventDto>('/events', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEventInput) =>
      api.patch<EventDto>(`/events/${id}`, input),
    onSuccess: (data) => {
      qc.setQueryData(eventKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}
